import { useState } from 'react';
import { supabase } from '../lib/supabase.ts';
import { friendlyError } from '../lib/friendlyError.ts';
import { btnSolid, input as inputCls } from '../lib/ui.ts';
import { FlapText } from '../components/FlapText.tsx';

/**
 * Shown after a user opens a password-reset OR invite link (App detects it via the
 * PASSWORD_RECOVERY event / the invite landing type — see authLanding.ts). The session is
 * already active, so setting the password is a single updateUser call. Branded to match the
 * Login screen (item D): the form sits on a raised .card with the blueprint grid showing
 * through, inputs are sunken wells, and a requirement hint + legible errors make a rejected
 * password obvious (Neil hit silent rejections in the invite test).
 */
const MIN_LEN = 8;

export function UpdatePassword({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_LEN) {
      setError(`Your password needs at least ${MIN_LEN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Those passwords don't match — re-enter them.");
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) {
      // Surface the server's reason legibly (e.g. a strength policy) — never a silent fail.
      setError(friendlyError(error, error.message));
      return;
    }
    // Password set; drop recovery/invite mode and continue into the app.
    onDone();
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 text-ink">
      <form onSubmit={submit} className="card w-full max-w-sm space-y-8 p-8">
        {/* Brand lockup — the Sorts Mill Goudy wordmark (via font-display), uppercase (brand §1). */}
        <div className="space-y-3 text-center">
          <div className="flex items-center justify-center gap-3">
            <h1 className="whitespace-nowrap font-display text-wordmark uppercase tracking-[-0.02em] leading-none text-ink">Alchmy Build</h1>
          </div>
          <p className="font-serif text-lg text-ink-2">Set a password</p>
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <input
              type="password"
              required
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`w-full ${inputCls}`}
            />
            <p className="text-xs text-ink-2">At least {MIN_LEN} characters.</p>
          </div>

          <input
            type="password"
            required
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={`w-full ${inputCls}`}
          />

          <button type="submit" disabled={busy} className={`w-full ${btnSolid}`}>
            <FlapText>{busy ? 'Saving…' : 'Save password'}</FlapText>
          </button>
        </div>

        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </div>
  );
}
